import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, MapPin, Pencil, Truck, XCircle } from "lucide-react";
import {
  api, fmt, getSessionPin, getSessionQuote, setSessionDelivery, clearSessionDelivery,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function DeliveryChecker({ inStock = true }) {
  const { user } = useAuth();
  const [pin, setPin] = useState("");
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const [changing, setChanging] = useState(false);

  const check = async (targetPin) => {
    const clean = (targetPin ?? pin).trim();
    if (!/^[1-9][0-9]{5}$/.test(clean)) {
      setError("Enter a valid 6-digit PIN code.");
      setQuote(null);
      return;
    }
    setChecking(true);
    setError("");
    try {
      // subtotal 0 → informational lookup only; checkout recalculates everything server-side
      const { data } = await api.post("/delivery/quote", { pin: clean, subtotal: 0 });
      if (data.serviceable) {
        setQuote(data);
        setSessionDelivery(clean, data);
        setChanging(false);
      } else {
        setQuote(null);
        setError(data.message || "Delivery isn't available at this PIN yet.");
      }
    } catch {
      setQuote(null);
      setError("Couldn't check delivery right now. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  // Session memory: show the previously checked PIN; otherwise prefill from the default saved address.
  useEffect(() => {
    const cached = getSessionQuote();
    const sessionPin = getSessionPin();
    if (cached?.serviceable && sessionPin) {
      setQuote(cached);
      setPin(sessionPin);
      return;
    }
    const def = (user?.addresses || []).find((a) => a.is_default) || (user?.addresses || [])[0];
    if (def?.pin) {
      setPin(def.pin);
      check(def.pin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const reset = () => {
    clearSessionDelivery();
    setQuote(null);
    setPin("");
    setError("");
    setChanging(true);
  };

  const threshold = quote ? fmt(quote.threshold || 1000) : null;

  return (
    <div className="mt-6 rounded-2xl border border-line bg-hover p-4" data-testid="pin-checker">
      <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-3">
        <Truck className="h-3.5 w-3.5 text-amber-500" />
        Check delivery to your PIN
      </p>

      {quote && !changing ? (
        <div className="mt-3" data-testid="pin-checker-result">
          <div className="flex items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-sm font-bold text-emerald-400">
              <CheckCircle2 className="h-4 w-4" />
              Delivery available to {quote.pin}{quote.label && <span className="font-semibold text-2" data-testid="pin-checker-locality"> · {quote.label}</span>}
            </p>
            <button data-testid="pin-checker-change" onClick={reset} className="flex items-center gap-1 text-[11px] font-bold text-3 transition-colors hover:text-amber-400">
              <Pencil className="h-3 w-3" /> Change PIN
            </button>
          </div>
          <div className="mt-2.5 space-y-1 text-xs text-2">
            <p><span className="text-3">Zone:</span> <span className="font-bold text-1" data-testid="pin-checker-zone">{quote.zone_label}{quote.zone === "origin" ? " · Origin" : ""}</span></p>
            {inStock ? (
              <p><span className="text-3">Estimated delivery:</span> <span className="font-bold text-1" data-testid="pin-checker-estimate">{quote.standard.estimate.text}</span></p>
            ) : (
              <p className="font-semibold text-amber-400" data-testid="pin-checker-oos">Product currently out of stock — delivery estimate shown once restocked.</p>
            )}
            {quote.free_delivery_eligible ? (
              <p><span className="text-3">Delivery charge:</span> <span className="font-bold text-emerald-400" data-testid="pin-checker-freenote">FREE on eligible orders {threshold}+</span></p>
            ) : (
              <p><span className="text-3">Delivery charge:</span> <span className="font-bold text-1" data-testid="pin-checker-charge">{fmt(quote.standard.charge)}</span></p>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-3">
          <div className="flex gap-2">
            <input
              data-testid="pin-checker-input"
              value={pin}
              onChange={(e) => { setPin(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
              onKeyDown={(e) => e.key === "Enter" && check()}
              placeholder="Enter PIN code"
              inputMode="numeric"
              maxLength={6}
              className="h-11 w-full max-w-[180px] rounded-xl border border-line bg-input px-4 font-mono text-sm tracking-widest text-1 placeholder:font-sans placeholder:tracking-normal placeholder:text-[var(--pq-text3)] focus:border-amber-500/60 focus:outline-none"
            />
            <button
              data-testid="pin-checker-btn"
              onClick={() => check()}
              disabled={checking}
              className="flex h-11 items-center gap-2 rounded-xl bg-amber-500 px-5 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-60"
            >
              {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check"}
            </button>
          </div>
          {error && (
            <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-red-400" data-testid="pin-checker-error">
              <XCircle className="h-3.5 w-3.5" /> {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
